import { useEffect, useMemo, useState } from "react";
import { PlatformViewBanner, ManageTenantBtn, TenantColHeader, TenantColCell, applyTenantFilter, usePlatformView } from "@/components/platform-view/PlatformViewShell";
import { useTranslation } from "react-i18next";
import { Copy, Loader2, Plus, Send, Shield, X, Check, AlertTriangle, CalendarDays, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { PermLabel, usePermLabel } from "@/utils/permissions";
import { ProgramSalaryRows, buildProgramRule, emptyProgSal, type ProgSal } from "./ProgramSalaryRows";
import { WhatsAppButton } from "@/components/whatsapp/WhatsAppButton";

type Member = { membership_id: string; user: { id: string; email: string; full_name: string | null } | null; role: { id: string; code: string; name: string } | null; scope_branch_ids: string[]; is_active: boolean; tenant_id?: string | null; phone?: string | null };
type Invite = { id: string; email: string; role_id: string; role?: { code: string; name: string } | null; extra_permissions?: string[]; extra_permission_count?: number; expires_at: string; used_at: string | null; invite_link_mocked: string | null };
type Role = { id: string; code: string; name: string; is_system: boolean };

export default function Staff() {
    const { t } = useTranslation();
    const { hasPerm, me } = useAuth();
    const { filterTenantId } = usePlatformView();
    const [members, setMembers] = useState<Member[]>([]);
    const [invites, setInvites] = useState<Invite[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [showInvite, setShowInvite] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [permsFor, setPermsFor] = useState<Member | null>(null);
    const canInvite = hasPerm("staff.invite");
    const canEditPerms = hasPerm("staff.edit");

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterTenantId) params.set("filter_tenant_id", filterTenantId);
            const qs = params.toString() ? `?${params}` : "";
            const [ms, invs, rs] = await Promise.all([api.get(`/staff${qs}`), api.get(`/staff/invites${qs}`), api.get(`/roles${qs}`)]);
            setMembers(ms.data.items);
            setInvites(invs.data.items);
            setRoles(rs.data.items);
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [filterTenantId]);

    return (
        <div className="space-y-8" data-testid="staff-page">
            <PlatformViewBanner />
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("staff.title")}</h1>
                    <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{members.length}</p>
                </div>
                {canInvite && (
                    <button data-testid="staff-invite-btn" onClick={() => setShowInvite(true)} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                        <Plus size={16} /> {t("staff.invite")}
                    </button>
                )}
            </div>
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}

            <div className="surface-card overflow-hidden">
                {loading ? <div className="p-8 flex justify-center"><Loader2 className="animate-spin" size={22} /></div> : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                                <th className="px-4 py-2 font-medium">{t("common.name")}</th>
                                <TenantColHeader />
                                <th className="px-4 py-2 font-medium">{t("auth.email")}</th>
                                <th className="px-4 py-2 font-medium">{t("staff.role")}</th>
                                <th className="px-4 py-2 font-medium">{t("common.status")}</th>
                                <th className="px-4 py-2 font-medium text-right"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map((m) => {
                                const isSelf = !!me && !!m.user && m.user.id === me.user.id;
                                return (
                                    <tr key={m.membership_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`staff-row-${m.user?.email}`}>
                                        <td className="px-4 py-3" style={{ color: "var(--text)" }}>{m.user?.full_name || "—"}</td>
                                        <TenantColCell tenantId={m.tenant_id} testId={`tenant-cell-staff-${m.user?.email}`} />
                                        <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>
                                            <div>{m.user?.email}</div>
                                            {m.phone && <div className="flex items-center gap-2 text-xs mt-0.5" data-testid={`staff-phone-${m.user?.email}`}>{m.phone}<WhatsAppButton name={m.user?.full_name || m.user?.email || ""} phone={m.phone} type="staff" id={m.user?.id} testid={`staff-wa-btn-${m.user?.email}`} /></div>}
                                        </td>
                                        <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{m.role ? t(`role.${m.role.code}` as any, { defaultValue: m.role.name }) : "—"}</td>
                                        <td className="px-4 py-3"><span className={`status-pill ${m.is_active ? "status-success" : "status-neutral"}`}>{m.is_active ? t("common.active") : t("common.archived")}</span></td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <ManageTenantBtn tenantId={(m as any).tenant_id} testId={`manage-staff-${m.user?.email}`} nextPath="/settings/staff" />
                                                {canEditPerms && !isSelf && (
                                                    <button data-testid={`staff-perms-${m.user?.email}`} onClick={() => setPermsFor(m)}
                                                        className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--primary)" }}>
                                                        <Shield size={12} />{t("staff.perms.button")}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <div>
                <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--text)" }}>{t("staff.pending_invites")}</h2>
                <div className="surface-card overflow-hidden">
                    {invites.filter((i) => !i.used_at).length === 0 ? (
                        <div className="p-6 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="invites-empty">{t("staff.no_invites")}</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                                    <th className="px-4 py-2 font-medium">{t("auth.email")}</th>
                                    <th className="px-4 py-2 font-medium">{t("staff.link_mocked")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invites.filter((i) => !i.used_at).map((i) => (
                                    <tr key={i.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`invite-row-${i.email}`}>
                                        <td className="px-4 py-3" style={{ color: "var(--text)" }}>
                                            <div>{i.email}</div>
                                            <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                                                {i.role ? t(`role.${i.role.code}` as any, { defaultValue: i.role.name }) : "—"}
                                                {" · "}
                                                <span title={t("staff.invite_extra_count") as string}>+{i.extra_permission_count || 0} {t("staff.invite_extra_short")}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {i.invite_link_mocked ? (
                                                <div className="flex items-center gap-2">
                                                    <code className="font-mono text-xs break-all" style={{ color: "var(--info-fg)" }}>{i.invite_link_mocked}</code>
                                                    <button data-testid={`copy-link-${i.email}`} onClick={() => navigator.clipboard.writeText(`${window.location.origin}${i.invite_link_mocked}`)} className="p-1 rounded-md border" style={{ borderColor: "var(--border)" }}><Copy size={14} /></button>
                                                </div>
                                            ) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showInvite && <InviteDialog roles={roles} onClose={() => setShowInvite(false)} onSaved={() => { setShowInvite(false); load(); }} />}
            {permsFor && <PermissionsDialog member={permsFor} onClose={() => setPermsFor(null)} />}
        </div>
    );
}

function InviteDialog({ roles, onClose, onSaved }: { roles: Role[]; onClose: () => void; onSaved: () => void }) {
    const { t } = useTranslation();
    const permLabel = usePermLabel();
    const invitable = roles.filter((r) => r.code !== "institution_owner");
    const [email, setEmail] = useState("");
    const [roleId, setRoleId] = useState("");
    const [role, setRole] = useState<any>(null);
    const [catalog, setCatalog] = useState<string[]>([]);
    const [actorAllowed, setActorAllowed] = useState<Set<string>>(new Set());
    const [extras, setExtras] = useState<string[]>([]);
    const [permQ, setPermQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [link, setLink] = useState<string | null>(null);
    // Phase 2 — role-conditional extras.
    const [progs, setProgs] = useState<{ id: string; name: string }[]>([]);
    const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
    const [progIds, setProgIds] = useState<string[]>([]);
    const [groupIds, setGroupIds] = useState<string[]>([]);
    const [ws, setWs] = useState<{ enabled: boolean; weekdays: number[]; start_time: string; end_time: string }>({
        enabled: false, weekdays: [0, 2, 4], start_time: "09:00", end_time: "17:00",
    });
    const [sal, setSal] = useState<{ enabled: boolean; rule_type: "fixed_monthly" | "hourly" | "per_lesson" | "percentage"; amount: string; currency: string; teacher_share: string; company_share: string }>({
        enabled: false, rule_type: "fixed_monthly", amount: "", currency: "AZN",
        teacher_share: "60", company_share: "40",
    });
    const [salMode, setSalMode] = useState<"general" | "per_program">("general");
    const [progSal, setProgSal] = useState<Record<string, ProgSal>>({});
    // Phase BUG-1: derive role.code from the invitable list so the fields
    // render the moment the user picks a role. Waiting for /roles/{id} to
    // resolve was the cause of the "work schedule + salary fields don't
    // show up in production" bug: any 4xx/5xx on that endpoint (perms,
    // network, stale build) left `role` null and hid the whole accordion.
    const roleFromList = roles.find((r) => r.id === roleId) || null;
    const isTeacher = (role?.code || roleFromList?.code) === "teacher";
    const isPortalRole = ["parent", "payer"].includes(role?.code || roleFromList?.code || "");

    useEffect(() => {
        (async () => {
            try {
                const [cat, mine] = await Promise.all([
                    api.get("/roles/permissions-catalog"),
                    api.get("/me/permissions"),
                ]);
                setCatalog(cat.data.permissions || []);
                setActorAllowed(new Set(mine.data.effective_permissions || []));
            } catch (e: any) { setErr(formatApiError(e)); }
        })();
    }, []);
    // Load programs/groups only when the picker becomes relevant (teacher role).
    useEffect(() => {
        if (!isTeacher) return;
        if (progs.length && groups.length) return;
        (async () => {
            try {
                const [p, g] = await Promise.all([
                    api.get("/programs"), api.get("/groups"),
                ]);
                setProgs((p.data.items || []).map((x: any) => ({ id: x.id, name: x.name })));
                setGroups((g.data.items || []).map((x: any) => ({ id: x.id, name: x.name })));
            } catch { /* non-blocking */ }
        })();
    }, [isTeacher]);

    useEffect(() => {
        (async () => {
            if (!roleId) { setRole(null); return; }
            try {
                const r = await api.get(`/roles/${roleId}`);
                setRole(r.data);
            } catch { setRole(null); }
        })();
    }, [roleId]);

    const roleSet = useMemo(() => new Set((role?.permissions || []) as string[]), [role]);
    const availableExtras = useMemo(() => {
        const term = permQ.trim().toLowerCase();
        return catalog
            .filter(p => !roleSet.has(p) && actorAllowed.has(p))
            .filter(p => !term || p.toLowerCase().includes(term) || permLabel(p).toLowerCase().includes(term));
    }, [catalog, roleSet, actorAllowed, permQ, permLabel]);

    const toggleExtra = (p: string) => {
        setExtras(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
    };

    const submit = async () => {
        if (!roleId) { setErr(t("staff.invite_err.role_required")); return; }
        setLoading(true); setErr(null);
        try {
            const payload: any = {
                email, role_id: roleId, scope_branch_ids: [],
                extra_permissions: extras,
            };
            if (isTeacher && (progIds.length || groupIds.length)) {
                payload.teacher_assignments = { program_ids: progIds, group_ids: groupIds };
            }
            if (!isPortalRole && ws.enabled && ws.weekdays.length) {
                payload.work_schedule = { weekdays: ws.weekdays, start_time: ws.start_time, end_time: ws.end_time };
            }
            const perProgram = isTeacher && salMode === "per_program" && progIds.length > 0;
            if (!isPortalRole && sal.enabled && perProgram) {
                const program_rules: any[] = [];
                for (const pid of progIds) {
                    const res = buildProgramRule(pid, progSal[pid] || emptyProgSal(sal.currency));
                    if ("error" in res) {
                        const name = progs.find((p) => p.id === pid)?.name || "";
                        setErr(`${name}: ${t(res.error)}`);
                        setLoading(false); return;
                    }
                    program_rules.push(res.rule);
                }
                payload.salary = { mode: "per_program", program_rules };
            } else if (!isPortalRole && sal.enabled) {
                if (sal.rule_type === "percentage") {
                    const ts = parseFloat(sal.teacher_share);
                    const cs = parseFloat(sal.company_share);
                    if (isNaN(ts) || isNaN(cs) || ts < 0 || cs < 0 || ts > 100 || cs > 100) {
                        setErr(t("staff.invite_err.invalid_salary_amount"));
                        setLoading(false); return;
                    }
                    if (Math.abs((ts + cs) - 100) > 0.01) {
                        setErr(t("staff.invite_err.percent_sum_100"));
                        setLoading(false); return;
                    }
                    payload.salary = {
                        rule_type: "percentage",
                        teacher_share_percent: ts,
                        company_share_percent: cs,
                        currency: sal.currency,
                    };
                } else if (sal.amount) {
                    const minor = Math.round(parseFloat(sal.amount) * 100);
                    if (!isNaN(minor) && minor >= 0) {
                        payload.salary = { rule_type: sal.rule_type, amount_minor: minor, currency: sal.currency };
                    }
                }
            }
            const { data } = await api.post("/staff/invites", payload);
            const status = data?.email_result?.status;
            if (status === "sent") { toast.success(t("staff.email.sent_ok")); onSaved(); }
            else { setLink(data.invite_link); }
        } catch (e: any) {
            const raw = formatApiError(e);
            const map: Record<string, string> = {
                permission_blocked_by_platform: t("staff.perms.err.permission_blocked_by_platform"),
                cannot_grant_missing_permission: t("staff.perms.cannot_grant_missing"),
                cannot_invite_higher_privilege: t("staff.perms.cannot_grant_missing"),
                invalid_role: t("staff.invite_err.role_required"),
                teacher_assignments_require_teacher_role: t("staff.invite_err.teacher_only"),
                work_schedule_incomplete: t("staff.invite_err.work_schedule_incomplete"),
                invalid_salary_rule_type: t("staff.invite_err.invalid_salary_type"),
                invalid_salary_amount: t("staff.invite_err.invalid_salary_amount"),
                invalid_currency: t("staff.invite_err.invalid_currency"),
                percentage_shares_must_sum_to_100: t("staff.invite_err.percent_sum_100"),
                invalid_program: t("staff.invite_err.invalid_program"),
                program_not_assigned: t("staff.invite_err.program_not_assigned"),
                duplicate_program_rule: t("staff.invite_err.duplicate_program_rule"),
                program_rules_empty: t("staff.invite_err.program_rules_empty"),
                program_rules_require_teacher_role: t("staff.invite_err.teacher_only"),
            };
            setErr(map[raw] || raw);
        } finally { setLoading(false); }
    };
    const toggleWd = (w: number) => setWs((s) => ({ ...s, weekdays: s.weekdays.includes(w) ? s.weekdays.filter((x) => x !== w) : [...s.weekdays, w].sort() }));
    const toggleId = (arr: string[], id: string) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose} data-testid="invite-dialog">
            <div className="surface-card w-full max-w-3xl p-6 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("staff.invite")}</h2>
                    <button data-testid="invite-dialog-close" onClick={onClose} style={{ color: "var(--text-muted)" }}><X size={18} /></button>
                </div>
                {link ? (
                    <div className="space-y-3" data-testid="invite-success">
                        <div className="rounded-md p-3 text-sm" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }}>
                            <div className="font-medium mb-1">{t("staff.email.fallback_title")}</div>
                            <div className="text-xs mb-2 opacity-90">{t("staff.email.fallback_desc")}</div>
                            <code className="font-mono text-xs break-all">{link}</code>
                        </div>
                        <button data-testid="invite-close-after-send" onClick={onSaved} className="w-full rounded-md py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>{t("common.close")}</button>
                    </div>
                ) : (
                    <div className="space-y-3 overflow-hidden flex flex-col flex-1">
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("auth.email")}</div><input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></label>
                            <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("staff.role")} <span style={{ color: "var(--error-fg)" }}>*</span></div>
                                <select data-testid="invite-role-select" required value={roleId} onChange={(e) => setRoleId(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }}>
                                    <option value="">{t("staff.invite_role_placeholder")}</option>
                                    {invitable.map((r) => <option key={r.id} value={r.id}>{t(`role.${r.code}` as any, { defaultValue: r.name })}</option>)}
                                </select>
                            </label>
                        </div>
                        {role && (
                            <div className="rounded-md p-2 text-xs" style={{ background: "var(--input)" }} data-testid="invite-role-preview">
                                <div className="mb-1" style={{ color: "var(--text-muted)" }}>{t("staff.invite_role_perms", { count: (role.permissions || []).length })}</div>
                                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                                    {(role.permissions || []).slice(0, 30).map((p: string) => (
                                        <span key={p} className="text-[11px] rounded-md px-1.5 py-0.5" style={{ background: "var(--info-bg)", color: "var(--info-fg)" }}>
                                            <PermLabel code={p} />
                                        </span>
                                    ))}
                                    {(role.permissions || []).length > 30 && <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>+{(role.permissions || []).length - 30}</span>}
                                </div>
                            </div>
                        )}
                        <div>
                            <div className="text-xs mb-1 flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
                                {t("staff.invite_extra_perms_label")}
                                <span className="rounded-full px-1.5 py-0.5" style={{ background: "var(--success-bg)", color: "var(--success-fg)" }}>{extras.length}</span>
                            </div>
                            <input type="text" value={permQ} onChange={e => setPermQ(e.target.value)}
                                placeholder={t("staff.perms.search_placeholder")}
                                data-testid="invite-extra-search"
                                className="surface-input px-3 py-2 w-full text-sm mb-2" style={{ minHeight: 36 }} />
                            <div className="rounded-md border overflow-y-auto flex-1" style={{ borderColor: "var(--border)", maxHeight: 220 }} data-testid="invite-extra-picker">
                                {availableExtras.length === 0 && (
                                    <div className="p-3 text-xs" style={{ color: "var(--text-muted)" }}>{t("staff.invite_extra_empty")}</div>
                                )}
                                {availableExtras.map(p => {
                                    const on = extras.includes(p);
                                    return (
                                        <label key={p} className="flex items-center gap-2 px-3 py-1.5 border-b cursor-pointer hover:bg-[var(--selected-bg)]" style={{ borderColor: "var(--border)" }} data-testid={`invite-extra-${p}`}>
                                            <input type="checkbox" checked={on} onChange={() => toggleExtra(p)} />
                                            <span className="text-sm flex-1 truncate" style={{ color: "var(--text)" }}><PermLabel code={p} showCode /></span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        {err && <div className="text-sm rounded-md p-2" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="invite-error">{err}</div>}

                        {isTeacher && (
                            <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--border)" }} data-testid="invite-teacher-assign">
                                <div className="text-xs font-medium" style={{ color: "var(--text)" }}>{t("staff.invite.teacher_assignments")}</div>
                                <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("nav.programs")}</div>
                                <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
                                    {progs.length === 0 ? <span className="text-xs" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</span> : progs.map((p) => {
                                        const on = progIds.includes(p.id);
                                        return <button key={p.id} type="button" data-testid={`invite-prog-${p.id}`} onClick={() => setProgIds(toggleId(progIds, p.id))}
                                            className="text-xs rounded-full px-2 py-1 border" style={{ background: on ? "var(--selected-bg)" : "var(--input)", color: "var(--text)", borderColor: on ? "var(--primary)" : "var(--border)" }}>{p.name}</button>;
                                    })}
                                </div>
                                <div className="text-xs mt-2 mb-1" style={{ color: "var(--text-muted)" }}>{t("nav.groups")}</div>
                                <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
                                    {groups.length === 0 ? <span className="text-xs" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</span> : groups.map((g) => {
                                        const on = groupIds.includes(g.id);
                                        return <button key={g.id} type="button" data-testid={`invite-group-${g.id}`} onClick={() => setGroupIds(toggleId(groupIds, g.id))}
                                            className="text-xs rounded-full px-2 py-1 border" style={{ background: on ? "var(--selected-bg)" : "var(--input)", color: "var(--text)", borderColor: on ? "var(--primary)" : "var(--border)" }}>{g.name}</button>;
                                    })}
                                </div>
                            </div>
                        )}

                        {!isPortalRole && roleId && (
                            <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--border)" }} data-testid="invite-work-schedule">
                                <label className="flex items-center gap-2 text-xs font-medium" style={{ color: "var(--text)" }}>
                                    <CalendarDays size={12} />
                                    <input data-testid="invite-ws-enabled" type="checkbox" checked={ws.enabled} onChange={(e) => setWs({ ...ws, enabled: e.target.checked })} />
                                    {t("staff.invite.work_schedule")}
                                </label>
                                {ws.enabled && (
                                    <div className="space-y-2">
                                        <div className="flex flex-wrap gap-1">
                                            {[0, 1, 2, 3, 4, 5, 6].map((w) => {
                                                const on = ws.weekdays.includes(w);
                                                return <button key={w} type="button" data-testid={`invite-ws-wd-${w}`} onClick={() => toggleWd(w)}
                                                    className="text-xs rounded-full px-2 py-0.5 border" style={{ background: on ? "var(--selected-bg)" : "var(--input)", color: "var(--text)", borderColor: on ? "var(--primary)" : "var(--border)" }}>{t(`weekday.${w}` as any)}</button>;
                                            })}
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("groups.sched.start_time")}</div><input data-testid="invite-ws-start" type="time" value={ws.start_time} onChange={(e) => setWs({ ...ws, start_time: e.target.value })} className="surface-input px-2 py-1 w-full text-sm" style={{ minHeight: 36 }} /></label>
                                            <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("groups.sched.end_time")}</div><input data-testid="invite-ws-end" type="time" value={ws.end_time} onChange={(e) => setWs({ ...ws, end_time: e.target.value })} className="surface-input px-2 py-1 w-full text-sm" style={{ minHeight: 36 }} /></label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {!isPortalRole && roleId && (
                            <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--border)" }} data-testid="invite-salary">
                                <label className="flex items-center gap-2 text-xs font-medium" style={{ color: "var(--text)" }}>
                                    <Wallet size={12} />
                                    <input data-testid="invite-sal-enabled" type="checkbox" checked={sal.enabled} onChange={(e) => setSal({ ...sal, enabled: e.target.checked })} />
                                    {t("staff.invite.salary")}
                                </label>
                                {sal.enabled && isTeacher && progIds.length > 0 && (
                                    <div className="flex gap-1" data-testid="invite-sal-mode">
                                        {(["general", "per_program"] as const).map((m) => (
                                            <button key={m} type="button" data-testid={m === "general" ? "invite-sal-mode-general" : "invite-sal-mode-program"}
                                                onClick={() => setSalMode(m)}
                                                className="text-xs rounded-full px-3 py-1 border"
                                                style={{ background: salMode === m ? "var(--selected-bg)" : "var(--input)", color: "var(--text)", borderColor: salMode === m ? "var(--primary)" : "var(--border)" }}>
                                                {t(m === "general" ? "staff.invite.salary_mode_general" : "staff.invite.salary_mode_program")}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {sal.enabled && isTeacher && progIds.length > 0 && salMode === "per_program" && (
                                    <ProgramSalaryRows programs={progs.filter((p) => progIds.includes(p.id))} values={progSal}
                                        currency={sal.currency} onChange={(pid, v) => setProgSal((s) => ({ ...s, [pid]: v }))} />
                                )}
                                {sal.enabled && !(isTeacher && progIds.length > 0 && salMode === "per_program") && (
                                    <div className="grid grid-cols-3 gap-2">
                                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("staff.invite.salary_model")}</div>
                                            <select data-testid="invite-sal-model" value={sal.rule_type} onChange={(e) => setSal({ ...sal, rule_type: e.target.value as any })} className="surface-input px-2 py-1 w-full text-sm" style={{ minHeight: 36 }}>
                                                <option value="fixed_monthly">{t("staff.invite.salary_fixed")}</option>
                                                <option value="hourly">{t("staff.invite.salary_hourly")}</option>
                                                <option value="per_lesson">{t("staff.invite.salary_per_lesson")}</option>
                                                <option value="percentage">{t("staff.invite.salary_percentage")}</option>
                                            </select>
                                        </label>
                                        {sal.rule_type === "percentage" ? (
                                            <>
                                                <label className="block">
                                                    <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("staff.invite.teacher_share")}</div>
                                                    <input data-testid="invite-sal-teacher-share" inputMode="decimal" value={sal.teacher_share}
                                                        onChange={(e) => {
                                                            const v = e.target.value.replace(/[^0-9.]/g, "");
                                                            const n = parseFloat(v);
                                                            const cs = !isNaN(n) ? String(Math.max(0, Math.round((100 - n) * 100) / 100)) : sal.company_share;
                                                            setSal({ ...sal, teacher_share: v, company_share: cs });
                                                        }}
                                                        className="surface-input px-2 py-1 w-full text-sm" style={{ minHeight: 36 }} />
                                                </label>
                                                <label className="block">
                                                    <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("staff.invite.company_share")}</div>
                                                    <input data-testid="invite-sal-company-share" inputMode="decimal" value={sal.company_share}
                                                        onChange={(e) => {
                                                            const v = e.target.value.replace(/[^0-9.]/g, "");
                                                            const n = parseFloat(v);
                                                            const ts = !isNaN(n) ? String(Math.max(0, Math.round((100 - n) * 100) / 100)) : sal.teacher_share;
                                                            setSal({ ...sal, company_share: v, teacher_share: ts });
                                                        }}
                                                        className="surface-input px-2 py-1 w-full text-sm" style={{ minHeight: 36 }} />
                                                </label>
                                            </>
                                        ) : (
                                            <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("staff.invite.salary_amount")}</div><input data-testid="invite-sal-amount" inputMode="decimal" placeholder="0.00" value={sal.amount} onChange={(e) => setSal({ ...sal, amount: e.target.value.replace(/[^0-9.,]/g, "").replace(",", ".") })} className="surface-input px-2 py-1 w-full text-sm" style={{ minHeight: 36 }} /></label>
                                        )}
                                        <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("staff.invite.salary_currency")}</div><input data-testid="invite-sal-currency" maxLength={3} value={sal.currency} onChange={(e) => setSal({ ...sal, currency: e.target.value.toUpperCase() })} className="surface-input px-2 py-1 w-full text-sm uppercase" style={{ minHeight: 36 }} /></label>
                                    </div>
                                )}
                            </div>
                        )}

                        <button data-testid="invite-send-btn" disabled={loading || !email || !roleId} onClick={submit} className="w-full inline-flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>{loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} {t("staff.invite")}</button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ------------------- Permissions Dialog ------------------- //
type PermsResp = {
    user: { id: string; email: string; full_name: string | null };
    role: { id: string; code: string; name: string } | null;
    role_permissions: string[];
    overrides: { permission: string; mode: "grant" | "deny" }[];
    effective_permissions: string[];
    // Phase TAC-2 additions: platform-owner scope + per-perm source annotation.
    allowed_by_platform?: string[];
    per_perm_source?: Record<string, "role" | "override" | "blocked_by_platform" | "none">;
};

function PermissionsDialog({ member, onClose }: { member: Member; onClose: () => void }) {
    const { t } = useTranslation();
    const [data, setData] = useState<PermsResp | null>(null);
    const [catalog, setCatalog] = useState<string[]>([]);
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    // Local overrides map: permission → mode (grant/deny/inherit)
    const [local, setLocal] = useState<Record<string, "grant" | "deny" | "inherit">>({});
    const isOwner = member.role?.code === "institution_owner";

    useEffect(() => {
        (async () => {
            try {
                const [permsRes, catRes] = await Promise.all([
                    api.get(`/staff/${member.user!.id}/permissions`),
                    api.get("/roles/permissions-catalog"),
                ]);
                setData(permsRes.data);
                setCatalog(catRes.data.permissions || []);
                const m: Record<string, "grant" | "deny" | "inherit"> = {};
                for (const o of permsRes.data.overrides || []) m[o.permission] = o.mode;
                setLocal(m);
            } catch (e: any) { setErr(formatApiError(e)); }
        })();
    }, [member.user?.id]);

    // Group permissions by module prefix (finance.*, people.*, hr.*, ...).
    // Filter honours BOTH raw code and the localised human label so users can
    // search in their own language (e.g. "Əməkhaqqı" matches payroll.*).
    const permLabel = usePermLabel();
    const groups = useMemo(() => {
        const term = q.trim().toLowerCase();
        const filtered = catalog.filter(p => !term
            || p.toLowerCase().includes(term)
            || permLabel(p).toLowerCase().includes(term));
        const g: Record<string, string[]> = {};
        for (const p of filtered) {
            const head = p.split(".", 1)[0];
            (g[head] = g[head] || []).push(p);
        }
        return g;
    }, [catalog, q, permLabel]);

    const setMode = (perm: string, mode: "grant" | "deny" | "inherit") => {
        if (isOwner && mode === "deny") {
            toast.warning(t("staff.perms.cannot_deny_owner"));
            return;
        }
        setLocal(prev => {
            const next = { ...prev };
            if (mode === "inherit") delete next[perm]; else next[perm] = mode;
            return next;
        });
    };

    const save = async () => {
        if (!data) return;
        setBusy(true); setErr(null);
        try {
            // Compute delta: overrides currently on the server + local removed → send "inherit"
            const serverKeys = new Set((data.overrides || []).map(o => o.permission));
            const localKeys = new Set(Object.keys(local));
            const items: { permission: string; mode: "grant" | "deny" | "inherit" }[] = [];
            for (const k of localKeys) items.push({ permission: k, mode: local[k] });
            for (const k of serverKeys) if (!localKeys.has(k)) items.push({ permission: k, mode: "inherit" });
            await api.put(`/staff/${member.user!.id}/permissions`, { items });
            toast.success(t("staff.perms.saved"));
            // Re-fetch to sync
            const r = await api.get(`/staff/${member.user!.id}/permissions`);
            setData(r.data);
            const nm: Record<string, "grant" | "deny" | "inherit"> = {};
            for (const o of r.data.overrides || []) nm[o.permission] = o.mode;
            setLocal(nm);
        } catch (e: any) {
            const raw = formatApiError(e);
            const map: Record<string, string> = {
                cannot_override_self: t("staff.perms.cannot_self"),
                cannot_deny_institution_owner: t("staff.perms.cannot_deny_owner"),
                cannot_grant_missing_permission: t("staff.perms.cannot_grant_missing"),
                unknown_permission: t("staff.perms.unknown"),
                not_in_tenant: t("staff.perms.not_in_tenant"),
            };
            setErr(map[raw] || raw);
            toast.error(map[raw] || raw);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={onClose} data-testid="staff-perms-dialog">
            <div className="surface-card w-full max-w-3xl p-6 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h2 className="text-lg font-semibold inline-flex items-center gap-2" style={{ color: "var(--text)" }}>
                            <Shield size={18} style={{ color: "var(--primary)" }} />
                            {t("staff.perms.title")}
                        </h2>
                        <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                            {member.user?.full_name || member.user?.email} · {member.role ? t(`role.${member.role.code}` as any, { defaultValue: member.role.name }) : "—"}
                        </p>
                    </div>
                    <button data-testid="perms-dialog-close" onClick={onClose} style={{ color: "var(--text-muted)" }}><X size={18} /></button>
                </div>
                {!data && !err && <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>}
                {err && <div className="rounded-md p-3 text-sm mb-2" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="perms-err">{err}</div>}
                {isOwner && (
                    <div className="mb-3 rounded-md p-2 text-xs inline-flex items-center gap-1.5" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="perms-owner-note">
                        <AlertTriangle size={12} />{t("staff.perms.owner_note")}
                    </div>
                )}
                {data && (
                    <>
                        <div className="rounded-md p-2 text-xs mb-2 flex flex-wrap gap-3" style={{ background: "var(--input)" }} data-testid="perms-summary">
                            <span>{t("staff.perms.role_perm_count")}: <b style={{ color: "var(--text)" }}>{data.role_permissions.length}</b></span>
                            <span>{t("staff.perms.grants")}: <b style={{ color: "var(--success-fg)" }}>{Object.values(local).filter(v => v === "grant").length}</b></span>
                            <span>{t("staff.perms.denies")}: <b style={{ color: "var(--error-fg)" }}>{Object.values(local).filter(v => v === "deny").length}</b></span>
                            <span>{t("staff.perms.effective")}: <b style={{ color: "var(--text)" }}>{data.effective_permissions.length}</b></span>
                        </div>
                        <div className="mb-2">
                            <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder={t("staff.perms.search_placeholder")}
                                data-testid="perms-search"
                                className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 36 }} />
                        </div>
                        <div className="mb-2 rounded-md p-2 text-xs inline-flex items-center gap-1.5" style={{ background: "var(--info-bg)", color: "var(--info-fg)" }} data-testid="perms-filter-note">
                            <Shield size={12} />{t("staff.perms.filter_active")}
                        </div>
                        <div className="overflow-y-auto flex-1 -mx-2 px-2">
                            {Object.entries(groups).sort(([a],[b]) => a.localeCompare(b)).map(([mod, perms]) => (
                                <div key={mod} className="mb-3">
                                    <div className="text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{t(`nav.${mod}`, mod)}</div>
                                    <div className="rounded-md border overflow-hidden" style={{ borderColor: "var(--border)" }}>
                                        {perms.map(p => {
                                            const inRole = data.role_permissions.includes(p);
                                            const mode = local[p]; // undefined | "grant" | "deny"
                                            const effective = (inRole || mode === "grant") && mode !== "deny";
                                            const source = data.per_perm_source?.[p] || "none";
                                            const blocked = source === "blocked_by_platform";
                                            const sourceLabel = t(`staff.perms.source.${source}` as any);
                                            const sourceColor = source === "role" ? "var(--info-fg)"
                                                : source === "override" ? "var(--success-fg)"
                                                : source === "blocked_by_platform" ? "var(--error-fg)"
                                                : "var(--text-muted)";
                                            const sourceBg = source === "role" ? "var(--info-bg)"
                                                : source === "override" ? "var(--success-bg)"
                                                : source === "blocked_by_platform" ? "var(--error-bg)"
                                                : "var(--input)";
                                            return (
                                                <div key={p} className="flex items-center gap-2 px-3 py-1.5 border-t first:border-t-0" style={{ borderColor: "var(--border)", opacity: blocked ? 0.65 : 1 }} data-testid={`perm-row-${p}`}>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm truncate" style={{ color: "var(--text)" }}><PermLabel code={p} showCode /></div>
                                                        <div className="text-[10px] flex flex-wrap gap-2 items-center mt-0.5" style={{ color: "var(--text-muted)" }}>
                                                            <span className="rounded-full px-1.5 py-0.5" style={{ background: sourceBg, color: sourceColor }} data-testid={`perm-source-${p}`}>{sourceLabel}</span>
                                                            {inRole ? <span>· {t("staff.perms.in_role")}</span> : <span>· {t("staff.perms.not_in_role")}</span>}
                                                            <span>·</span>
                                                            {effective && !blocked ? <span style={{ color: "var(--success-fg)" }}>{t("staff.perms.effective_yes")}</span> : <span style={{ color: "var(--text-muted)" }}>{t("staff.perms.effective_no")}</span>}
                                                            {blocked && <span className="italic">· {t("staff.perms.blocked_by_platform_note")}</span>}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-1" role="radiogroup" aria-label={p}>
                                                        {(["inherit","grant","deny"] as const).map(m => (
                                                            <button key={m} data-testid={`perm-${p}-${m}`} onClick={() => setMode(p, m)}
                                                                disabled={blocked}
                                                                className="text-[11px] rounded-md px-2 py-1 border disabled:opacity-50 disabled:cursor-not-allowed"
                                                                style={{
                                                                    background: (mode || "inherit") === m
                                                                        ? (m === "grant" ? "var(--success-bg)" : m === "deny" ? "var(--error-bg)" : "var(--selected-bg)")
                                                                        : "transparent",
                                                                    color: (mode || "inherit") === m
                                                                        ? (m === "grant" ? "var(--success-fg)" : m === "deny" ? "var(--error-fg)" : "var(--text)")
                                                                        : "var(--text-muted)",
                                                                    borderColor: "var(--border)",
                                                                }}>
                                                                {t(`staff.perms.mode.${m}`)}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 flex justify-end gap-2">
                            <button onClick={onClose} className="rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text-muted)", minHeight: 40 }}>{t("common.close")}</button>
                            <button data-testid="perms-save" onClick={save} disabled={busy}
                                className="rounded-md px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
                                style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                {t("staff.perms.save")}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
